import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { STANDARD_DATE_FORMAT } from 'src/shared/constants/dateConstants';
import cmsService from 'src/services/cms-service';
import { CMSHabitatAssayType, FeatureType } from 'src/shared/types';
import MapCard from 'src/shared/components/map-card/MapCard';
import { countries } from 'src/shared/data/countries';
import Chip from 'src/shared/components/chip/Chip';
import usePermissions from 'src/shared/hooks/usePermissions';
import useDemoProjectAnalytics from 'src/shared/components/demo-project-banner/hooks/useDemoProjectAnalytics';
import TestTypeChip from 'src/shared/components/test-type-chip/TestTypeChip';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useFeature from 'src/shared/hooks/useFeature';
import { useAppDispatch } from 'src/store';
import styles from './ProjectListItem.module.scss';
import { setCurrentProjectId } from '../../state/projectListSlice';
import type { ProjectCardData } from '../../state/api/queries/projectsListQuery';

export type ProjectListItemProps = {
    project: ProjectCardData;
};

const ProjectListItem = (props: ProjectListItemProps) => {
    const { project } = props;
    const projectId = project.projectId;
    const dispatch = useAppDispatch();

    const { isCurrentUserInternal } = usePermissions();
    const { hasFeature } = useFeature();
    const navigate = useAppNavigation();
    const [isDemoProject, setIsDemoProject] = useState<boolean>(false);
    const { trackDemoProjectSelection } = useDemoProjectAnalytics();
    const hasHabitatInsightsFeature = hasFeature({
        feature: FeatureType.HLCA,
        projectId: projectId,
    });
    const hasSurveyDesignFeature = hasFeature({
        feature: FeatureType.SURVEY,
        projectId: projectId,
    });

    useEffect(() => {
        const fetchProjectDetailsfromCms = async () => {
            const { isDemo } = (await cmsService.getProjectById(projectId)) || false;
            setIsDemoProject(isDemo);
        };
        fetchProjectDetailsfromCms();
    }, [projectId]);

    const isBasicSubscription = project.subscription?.name === 'Basic';

    const setDateFormat = (dateStr: string) => {
        const dateObject = new Date(dateStr);
        return moment(dateObject).format(STANDARD_DATE_FORMAT);
    };

    const hasEDnaData = project.samplesCount > 0;
    const countryInfo = project.country ? countries[project.country] : undefined;
    const countryCode = countryInfo ? countryInfo[0] : undefined;
    const projectStartDate = project.projectStartDate ? setDateFormat(project.projectStartDate) : undefined;
    const habitatsAssays = (project.habitatAssays ?? []).map(ha => ({
        habitatName: ha.habitatName ?? '',
        assayName: ha.assayName ?? '',
        habitatAssayKey: ha.habitatAssayKey ?? '',
    })) as CMSHabitatAssayType[];
    const uniqueHabitatAssays = habitatsAssays.filter((item, index, source) => {
        return source.findIndex(candidate => candidate.habitatAssayKey === item.habitatAssayKey) === index;
    });
    const assayKeyTokens = uniqueHabitatAssays
        .map(item => item.habitatAssayKey?.toLowerCase())
        .filter((token): token is string => Boolean(token));
    const metaEntries: Array<{ label: string; value: string }> = [];

    if (projectStartDate) {
        metaEntries.push({
            label: 'Started',
            value: projectStartDate,
        });
    }

    if (typeof project.samplesCount === 'number' && project.features?.includes(FeatureType.EDNA)) {
        metaEntries.push({
            label: 'Samples',
            value: project.samplesCount.toLocaleString(),
        });
    }

    if (project.country) {
        metaEntries.push({
            label: 'Location',
            value: project.country,
        });
    }
    const featureChipConfigs = [
        {
            label: 'Species Insights',
            backgroundColor: '#076769',
            testId: `species-insights-tag-${project.projectId}`,
            isVisible: hasEDnaData,
            skipTokens: ['species', 'insight'],
        },
        {
            label: 'Survey design',
            backgroundColor: '#073A69',
            testId: `survey-design-tag-${project.projectId}`,
            isVisible: hasSurveyDesignFeature,
            skipTokens: ['survey'],
        },
        {
            label: 'Habitat Insights',
            backgroundColor: '#008C6A',
            testId: `habitat-insights-tag-${project.projectId}`,
            isVisible: hasHabitatInsightsFeature,
            skipTokens: ['habitat', 'insight'],
        },
    ] as const;

    const featureChips = featureChipConfigs.filter(config => {
        if (!config.isVisible) {
            return false;
        }

        if (!config.skipTokens?.length) {
            return true;
        }

        const overlapsWithAssay = config.skipTokens.some(token => assayKeyTokens.some(assayToken => assayToken.includes(token)));
        return !overlapsWithAssay;
    });

    const featureChipSection = isBasicSubscription
        ? {
              label: 'Subscription',
              content: (
                  <div className={styles.chips}>
                      <Chip label='Starter' backgroundColor='#3EA8A6' testId={`starter-tag-${project.projectId}`} />
                  </div>
              ),
          }
        : featureChips.length > 0
        ? {
              label: 'Highlights',
              content: (
                  <div className={styles.chips}>
                      {featureChips.map(chip => (
                          <Chip key={chip.testId} label={chip.label} backgroundColor={chip.backgroundColor} testId={chip.testId} />
                      ))}
                  </div>
              ),
          }
        : null;

    const onSelectProject = async () => {
        if (isDemoProject) {
            trackDemoProjectSelection(project.projectName);
        }

        dispatch(setCurrentProjectId(project.projectId));

        navigate.toSiteSummary({
            projectId: projectId,
        });
    };

    return (
        <article
            className={styles.pojectListItem}
            key={project.projectId}
            data-testid={`project-card-${project.projectId}`}
            onClick={onSelectProject}
        >
            <div className={styles.contentSection}>
                <div className={styles.titleRow}>
                    <div className={styles.titleGroup}>
                        <h3 className={styles.cardTitle} data-testid={`project-card-title-${project.projectId}`}>
                            {project.projectName}
                        </h3>
                        {isCurrentUserInternal ? <div className={styles.cardSubTitle}>Project code: {project.projectCode}</div> : null}
                    </div>
                </div>

                {metaEntries.length > 0 ? (
                    <dl className={styles.metaInfo}>
                        {metaEntries.map(entry => (
                            <div className={styles.metaChip} key={entry.label}>
                                <dt>{entry.label}</dt>
                                <dd>{entry.value}</dd>
                            </div>
                        ))}
                    </dl>
                ) : null}

                {featureChipSection ? (
                    <div className={styles.featureSection}>
                        <span className={styles.sectionLabel}>{featureChipSection.label}</span>
                        {featureChipSection.content}
                    </div>
                ) : null}

                <div className={styles.mapSection}>
                    <MapCard boundsArray={countryInfo?.[1] ?? [89, 179, -89, -179]} />
                    {countryCode ? <span className={styles.countryBadge}>{countryCode}</span> : null}
                    <div className={styles.mapOverlay}>
                        <span className={styles.overlayTitle}>View project</span>
                        <span className={styles.overlaySubtitle}>Open site summary</span>
                    </div>
                </div>

                {Boolean(uniqueHabitatAssays.length) && (
                    <div className={styles.testTypesWrapper}>
                        <span className={styles.sectionLabel}>Data source(s)</span>
                        <div className={styles.testTypes}>
                            {uniqueHabitatAssays.map((item: CMSHabitatAssayType, index: number) => {
                                return (
                                    <TestTypeChip
                                        key={item.habitatAssayKey || index}
                                        testTypeKey={item.habitatAssayKey}
                                        testId={`project-card-test-type-${item.habitatAssayKey}-${project.projectId}`}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
};

export default ProjectListItem;
